export interface AdapterMessage {
  role: "user" | "assistant";
  content: string;
}

// Fired with each new slice of reply text as it streams in — never a synthesized addition
// (e.g. Anthropic's "[Reply cut off...]" note is only ever appended to the final resolved
// AdapterReply.text, never pushed through here), always real, in-order provider output. Optional
// on every adapter's sendMessage — omitting it still returns the same final AdapterReply, just
// without incremental updates along the way.
export type OnChunk = (deltaText: string) => void;

// The subset of ProviderConfig (core/types.ts) an adapter's sendMessage/listModels actually
// needs — kept as its own narrower type here rather than importing ProviderConfig directly, so
// adapters don't depend on unrelated fields (e.g. UI-only ones) added to that type later.
export interface MinimalProviderConfig {
  name: string;
  endpoint: string;
  apiKey: string;
  model: string;
  temperature?: number;
  systemPrompt?: string;
  maxOutputTokens?: number;
}

// Populated from whatever usage field the provider's response included (Anthropic's
// top-level "usage", OpenAI-compatible's "usage", Gemini's "usageMetadata"). inputTokens is
// the size of the *entire prompt* sent for that one request — since none of these APIs keep
// server-side conversation state, every request resends the full truncated history, so this
// is not just "the new message" but everything resent alongside it. That's also why it's the
// right number to sum across turns for a running total: each request really is billed for
// resending that history, not just the incremental new text.
export interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
}

export interface AdapterReply {
  text: string;
  usage?: TokenUsage;
}

// contextTokens is populated only for providers whose API returns it directly (Gemini's
// inputTokenLimit, Anthropic's max_input_tokens) — undefined for the rest, in which case the
// caller falls back to name-pattern matching or a conservative default (see
// core/modelContextSizes.ts and storage.ts's getDefaultMaxContextTokens). maxOutputTokens is the
// same idea for the reply-length cap — currently only populated for Anthropic (its Models API
// returns "max_tokens" per model alongside "max_input_tokens"); undefined elsewhere, in which
// case the caller falls back to DEFAULT_ANTHROPIC_MAX_TOKENS (storage.ts's
// getDefaultMaxOutputTokens) — no name-pattern tier for this one, unlike context, since it's
// only ever relevant for one adapter and a live API value is available whenever a fetch has
// actually happened.
export interface ModelInfo {
  id: string;
  contextTokens?: number;
  maxOutputTokens?: number;
}

// Remembers each model's real context size (ModelInfo.contextTokens, whichever of the three
// adapters' listModels() actually populated it — Gemini's inputTokenLimit, Anthropic's
// max_input_tokens, Kimi's context_length) for the rest of this app session. In-memory only,
// never written into ProviderConfig itself, so it stays genuinely live rather than a saved
// snapshot — same reasoning and shape as anthropic.ts's own liveMaxOutputTokensCache (added
// 2026-08-12 for Max reply length; this is the equivalent for Max context, generalized here
// since three adapters populate it and storage.ts — not an adapter — is what reads it, unlike
// the reply-length cache which only ever needed to live inside anthropic.ts itself).
const liveContextTokensCache = new Map<string, number>();

function contextCacheKey(endpoint: string, model: string): string {
  return `${endpoint} ${model}`;
}

// Called by each adapter's listModels() for every model it returns with a known contextTokens —
// harmless no-op to call with undefined (see call sites), so callers don't need their own guard.
export function recordLiveContextTokens(
  endpoint: string,
  model: string,
  contextTokens: number | undefined
): void {
  if (contextTokens !== undefined) {
    liveContextTokensCache.set(contextCacheKey(endpoint, model), contextTokens);
  }
}

// Read by storage.ts's getDefaultMaxContextTokens/isModelSpecificMaxContext as a fallback
// whenever they weren't explicitly handed a live number — see those functions' own comments.
export function getLiveContextTokens(endpoint: string, model: string): number | undefined {
  return liveContextTokensCache.get(contextCacheKey(endpoint, model));
}

export class ProviderApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ProviderApiError";
  }
}

export function createProviderApiError(message: string): ProviderApiError {
  return new ProviderApiError(message);
}

export function isProviderApiError(err: unknown): err is ProviderApiError {
  return err instanceof ProviderApiError;
}

export function normalizeEndpoint(endpoint: string): string {
  return endpoint.replace(/\/+$/, "");
}

// Shared by all three adapters' two network-error catch sites each (the initial httpStream call,
// and the SSE frame-reading loop) — six near-identical copies of this before it was factored out
// here. An AbortError (the Stop button / clicking the typing bubble, see chat.ts) passes straight
// through unwrapped — chat.ts's catch blocks need the real DOMException name to tell user
// cancellation apart from any other failure, and wrapping it in a ProviderApiError here would
// hide that behind a generic message instead. Anything else becomes a ProviderApiError with the
// same "Network error contacting X" wording every adapter already used.
export function wrapNetworkError(providerName: string, err: unknown): never {
  if (err instanceof DOMException && err.name === "AbortError") {
    throw err;
  }
  throw createProviderApiError(
    `Network error contacting ${providerName}: ${(err as Error).message}`
  );
}

// Shared by all three adapters' error paths (a non-ok response, or a listModels failure) — every
// provider here follows the same convention of a JSON body shaped like { error: { message } },
// falling back to the raw HTTP status text when the body isn't JSON or doesn't have that shape.
// Typed structurally (not against HttpResponse directly) so it works unchanged against either
// HttpResponse (httpRequest, non-streaming) or HttpStreamResponse's own error path
// (httpStream — see http.ts; only its json()/statusText are ever touched on a non-ok response,
// never the streaming body-reader path).
export async function safeErrorDetail(response: {
  json: () => Promise<unknown>;
  statusText: string;
}): Promise<string> {
  try {
    const data = (await response.json()) as { error?: { message?: string } };
    return data?.error?.message || response.statusText;
  } catch {
    return response.statusText;
  }
}
